import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories, getHotArticles, getNewArticles } from '@/mocks/articles';
import { siteConfig, faqData } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';

const heroImage = 'https://readdy.ai/api/search-image?query=A%20dramatic%20and%20stylish%20red%20carpet%20entrance%20with%20flashing%20paparazzi%20cameras%2C%20golden%20spotlights%2C%20glamorous%20Hollywood%20vibe%2C%20dark%20background%20with%20warm%20golden%20and%20crimson%20accents%2C%20cinematic%20atmosphere%2C%20luxurious%20and%20mysterious%20mood%2C%20no%20visible%20people%2C%20abstract%20entertainment%20industry%20theme&width=1920&height=900&seq=hero-bg-01&orientation=landscape';

const categoryImages: Record<string, string> = {
  'mingxing-chouwen': 'https://readdy.ai/api/search-image?query=A%20dramatic%20breaking%20news%20scene%20with%20red%20alarm%20lights%2C%20dark%20moody%20atmosphere%2C%20paparazzi%20flash%2C%20celebrity%20scandal%20theme%2C%20cinematic%20style%2C%20deep%20crimson%20and%20black%20tones%2C%20no%20people&width=600&height=400&seq=cat-img-01&orientation=landscape',
  'lianqing-baoguang': 'https://readdy.ai/api/search-image?query=A%20romantic%20candlelit%20dinner%20setting%20with%20two%20empty%20chairs%2C%20soft%20warm%20lighting%2C%20rose%20petals%2C%20elegant%20restaurant%20atmosphere%2C%20mysterious%20and%20intimate%20vibe%2C%20golden%20and%20pink%20tones%2C%20no%20people&width=600&height=400&seq=cat-img-02&orientation=landscape',
  'quannei-miwen': 'https://readdy.ai/api/search-image?query=A%20shadowy%20backstage%20area%20with%20dramatic%20curtains%2C%20mysterious%20spotlight%2C%20secretive%20atmosphere%2C%20dark%20corners%2C%20theater%20backstage%20vibe%2C%20deep%20purple%20and%20black%20tones%2C%20no%20people&width=600&height=400&seq=cat-img-03&orientation=landscape',
  'zongyi-xianchang': 'https://readdy.ai/api/search-image?query=A%20TV%20studio%20set%20with%20colorful%20stage%20lights%2C%20empty%20judges%20panel%2C%20variety%20show%20atmosphere%2C%20bright%20neon%20accents%2C%20entertainment%20production%20vibe%2C%20vibrant%20and%20lively%20mood%2C%20no%20people&width=600&height=400&seq=cat-img-04&orientation=landscape',
  'yingshi-neimu': 'https://readdy.ai/api/search-image?query=A%20film%20set%20with%20professional%20camera%20equipment%2C%20director%20chair%2C%20clapperboard%2C%20cinematic%20lighting%2C%20movie%20production%20atmosphere%2C%20warm%20cinematic%20tones%2C%20behind%20the%20scenes%20vibe%2C%20no%20people&width=600&height=400&seq=cat-img-05&orientation=landscape',
};

export default function HomePage() {
  const hotArticles = getHotArticles();
  const newArticles = getNewArticles();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteConfig.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: siteConfig.address,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.phone,
      contactType: 'customer service',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src={heroImage}
              alt="黑料不打烊 - 娱乐圈劲爆内幕"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full w-full text-center px-4">
              <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-sm font-semibold rounded-full mb-5 tracking-wider">
                24小时 · 不打烊
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 max-w-3xl leading-tight">
                {siteConfig.tagline}
              </h1>
              <p className="text-base md:text-lg text-background-200/90 max-w-2xl leading-relaxed">
                {siteConfig.description}
              </p>
              <div className="flex items-center gap-4 mt-8">
                <Link
                  to={`/category/${categories[0].slug}`}
                  className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap"
                >
                  立即吃瓜
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 border border-background-200/40 text-background-50 font-medium rounded-full hover:bg-background-50/15 transition-all duration-300 text-sm whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Hot Articles Section */}
          <section className="px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  热门爆料
                </h2>
                <span className="px-2 py-0.5 text-xs font-semibold bg-accent-100 text-accent-700 rounded-full">HOT</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {hotArticles.slice(0, 3).map((article, index) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    coverImage={article.coverImage}
                    category={article.category}
                    categoryName={categories.find((c) => c.slug === article.category)?.name || ''}
                    author={article.author}
                    publishedAt={article.publishedAt}
                    tags={article.tags}
                    isHot={article.isHot}
                    isNew={article.isNew}
                    viewCount={article.viewCount}
                    commentCount={article.commentCount}
                    variant={index === 0 ? 'featured' : 'default'}
                  />
                ))}
              </div>

              {hotArticles.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                  {hotArticles.slice(3).map((article) => (
                    <ArticleCard
                      key={article.id}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      coverImage={article.coverImage}
                      category={article.category}
                      categoryName={categories.find((c) => c.slug === article.category)?.name || ''}
                      author={article.author}
                      publishedAt={article.publishedAt}
                      tags={article.tags}
                      isHot={article.isHot}
                      isNew={article.isNew}
                      viewCount={article.viewCount}
                      commentCount={article.commentCount}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Category Showcase */}
          <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  爆料分类
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group relative rounded-lg overflow-hidden h-36 md:h-44"
                  >
                    <img
                      src={categoryImages[cat.slug]}
                      alt={cat.name}
                      className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className={`${cat.icon} text-sm text-background-50`}></i>
                        <span className="font-heading text-sm font-bold text-background-50">{cat.name}</span>
                      </div>
                      <p className="text-xs text-background-300/80 line-clamp-1">
                        {cat.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Scoops */}
          <section className="px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  最新爆料
                </h2>
                <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-700 rounded-full">NEW</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {newArticles.slice(0, 6).map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    coverImage={article.coverImage}
                    category={article.category}
                    categoryName={categories.find((c) => c.slug === article.category)?.name || ''}
                    author={article.author}
                    publishedAt={article.publishedAt}
                    tags={article.tags}
                    isHot={article.isHot}
                    isNew={article.isNew}
                    viewCount={article.viewCount}
                    commentCount={article.commentCount}
                  />
                ))}
              </div>

              <div className="flex justify-center mt-8">
                <Link
                  to={`/category/${categories[0].slug}`}
                  className="px-6 py-3 bg-secondary-100 text-secondary-700 font-medium rounded-full hover:bg-secondary-200 transition-all duration-300 text-sm whitespace-nowrap"
                >
                  查看更多爆料 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-8 justify-center">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  常见问题
                </h2>
              </div>

              <div className="flex flex-col gap-3">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="font-medium text-foreground-900 text-sm">{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                      }`}
                    >
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}